import { useState, useEffect } from 'react';
import {
  getCustomer,
  createCustomer,
  listPaymentMethods,
  addPaymentMethod,
  deletePaymentMethod,
  setDefaultPaymentMethod,
  listCompanyEscrows,
  releaseEscrow,
  refundEscrow,
  listTransactions,
} from '../../services/payments';
import type {
  StripeCustomer,
  PaymentMethod,
  EscrowHold,
  Transaction,
  CreateCustomerRequest,
} from '../../types/payment';
import {
  formatCurrency,
  getEscrowStatusLabel,
  getEscrowStatusColor,
  getTransactionTypeLabel,
} from '../../types/payment';

export default function PaymentsPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Data state
  const [customer, setCustomer] = useState<StripeCustomer | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [escrows, setEscrows] = useState<EscrowHold[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);

  // UI state
  const [activeTab, setActiveTab] = useState<'escrows' | 'methods' | 'history'>('escrows');
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [showAddMethodModal, setShowAddMethodModal] = useState(false);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      setLoading(true);
      setError(null);

      // Try to get customer
      try {
        const customerData = await getCustomer();
        setCustomer(customerData);

        // Load other data if customer exists
        const [methodsData, escrowsData, transactionsData] = await Promise.all([
          listPaymentMethods(),
          listCompanyEscrows(1, 20),
          listTransactions({ page: 1, page_size: 20 }),
        ]);
        setPaymentMethods(methodsData);
        setEscrows(escrowsData.escrows);
        setTransactions(transactionsData.transactions);
      } catch (e: unknown) {
        // No customer yet
        if ((e as { response?: { status?: number } })?.response?.status === 404) {
          setCustomer(null);
        } else {
          throw e;
        }
      }
    } catch (e: unknown) {
      setError((e as Error).message || 'Failed to load payment data');
    } finally {
      setLoading(false);
    }
  }

  async function handleCreateCustomer(data: CreateCustomerRequest) {
    try {
      setProcessing(true);
      const customerData = await createCustomer(data);
      setCustomer(customerData);
      setShowSetupModal(false);
      setSuccess('Billing account created successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (e: unknown) {
      setError((e as Error).message || 'Failed to create billing account');
    } finally {
      setProcessing(false);
    }
  }

  async function handleAddPaymentMethod(paymentMethodId: string) {
    try {
      setProcessing(true);
      const method = await addPaymentMethod({
        payment_method_id: paymentMethodId,
        set_default: paymentMethods.length === 0,
      });
      setPaymentMethods([...paymentMethods, method]);
      setShowAddMethodModal(false);
      setSuccess('Payment method added');
      setTimeout(() => setSuccess(null), 3000);
    } catch (e: unknown) {
      setError((e as Error).message || 'Failed to add payment method');
    } finally {
      setProcessing(false);
    }
  }

  async function handleDeleteMethod(id: string) {
    if (!confirm('Are you sure you want to remove this payment method?')) return;

    try {
      await deletePaymentMethod(id);
      setPaymentMethods(paymentMethods.filter((m) => m.id !== id));
      setSuccess('Payment method removed');
      setTimeout(() => setSuccess(null), 3000);
    } catch (e: unknown) {
      setError((e as Error).message || 'Failed to remove payment method');
    }
  }

  async function handleSetDefault(id: string) {
    try {
      await setDefaultPaymentMethod(id);
      setPaymentMethods(
        paymentMethods.map((m) => ({
          ...m,
          is_default: m.id === id,
        }))
      );
      setSuccess('Default payment method updated');
      setTimeout(() => setSuccess(null), 3000);
    } catch (e: unknown) {
      setError((e as Error).message || 'Failed to update default method');
    }
  }

  async function handleReleaseEscrow(id: string) {
    if (!confirm('Are you sure you want to release these funds to the expert?')) return;

    try {
      setProcessing(true);
      const updated = await releaseEscrow(id);
      setEscrows(escrows.map((e) => (e.id === id ? updated : e)));
      setSuccess('Funds released successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (e: unknown) {
      setError((e as Error).message || 'Failed to release funds');
    } finally {
      setProcessing(false);
    }
  }

  async function handleRefundEscrow(id: string) {
    if (!confirm('Are you sure you want to refund this escrow?')) return;

    try {
      setProcessing(true);
      const updated = await refundEscrow(id);
      setEscrows(escrows.map((e) => (e.id === id ? updated : e)));
      setSuccess('Escrow refunded');
      setTimeout(() => setSuccess(null), 3000);
    } catch (e: unknown) {
      setError((e as Error).message || 'Failed to refund escrow');
    } finally {
      setProcessing(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // No customer setup yet
  if (!customer) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <h1 className="text-2xl font-bold mb-6">Payments</h1>

        <div className="bg-white rounded-lg shadow p-8 text-center">
          <div className="mb-6">
            <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
          </div>

          <h2 className="text-xl font-semibold mb-2">Set Up Billing</h2>
          <p className="text-gray-600 mb-6">
            Configure your billing details to fund milestones and pay experts.
          </p>

          <button
            onClick={() => setShowSetupModal(true)}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Set Up Billing
          </button>
        </div>

        {showSetupModal && (
          <SetupBillingModal
            onClose={() => setShowSetupModal(false)}
            onSubmit={handleCreateCustomer}
            processing={processing}
          />
        )}
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <h1 className="text-2xl font-bold mb-6">Payments</h1>

      {error && (
        <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">
            Dismiss
          </button>
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-50 text-green-700 rounded-lg">
          {success}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-8">
          <TabButton
            active={activeTab === 'escrows'}
            onClick={() => setActiveTab('escrows')}
          >
            Escrows ({escrows.length})
          </TabButton>
          <TabButton
            active={activeTab === 'methods'}
            onClick={() => setActiveTab('methods')}
          >
            Payment Methods ({paymentMethods.length})
          </TabButton>
          <TabButton
            active={activeTab === 'history'}
            onClick={() => setActiveTab('history')}
          >
            Transaction History
          </TabButton>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'escrows' && (
        <EscrowsTab
          escrows={escrows}
          onRelease={handleReleaseEscrow}
          onRefund={handleRefundEscrow}
          processing={processing}
        />
      )}

      {activeTab === 'methods' && (
        <PaymentMethodsTab
          methods={paymentMethods}
          onAdd={() => setShowAddMethodModal(true)}
          onDelete={handleDeleteMethod}
          onSetDefault={handleSetDefault}
        />
      )}

      {activeTab === 'history' && (
        <TransactionHistoryTab transactions={transactions} />
      )}

      {/* Add Payment Method Modal */}
      {showAddMethodModal && (
        <AddPaymentMethodModal
          onClose={() => setShowAddMethodModal(false)}
          onSubmit={handleAddPaymentMethod}
          processing={processing}
        />
      )}
    </div>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

function TabButton({ active, onClick, children }: TabButtonProps) {
  return (
    <button
      onClick={onClick}
      className={`pb-4 text-sm font-medium border-b-2 transition-colors ${
        active
          ? 'border-blue-600 text-blue-600'
          : 'border-transparent text-gray-500 hover:text-gray-700'
      }`}
    >
      {children}
    </button>
  );
}

interface EscrowsTabProps {
  escrows: EscrowHold[];
  onRelease: (id: string) => void;
  onRefund: (id: string) => void;
  processing: boolean;
}

function EscrowsTab({ escrows, onRelease, onRefund, processing }: EscrowsTabProps) {
  if (escrows.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <p>No escrows yet</p>
        <p className="text-sm mt-2">Escrows will appear here when you fund milestones</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expert Receives</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Platform Fee</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {escrows.map((escrow) => (
            <tr key={escrow.id}>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {escrow.description || 'Escrow Payment'}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                {formatCurrency(escrow.amount_cents, escrow.currency)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {formatCurrency(escrow.expert_amount_cents, escrow.currency)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {formatCurrency(escrow.platform_fee_cents, escrow.currency)} ({(escrow.platform_fee_rate * 100).toFixed(0)}%)
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <StatusBadge status={escrow.status} />
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {new Date(escrow.created_at).toLocaleDateString()}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm">
                {escrow.status === 'funded' && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => onRelease(escrow.id)}
                      disabled={processing}
                      className="text-green-600 hover:text-green-700 disabled:opacity-50"
                    >
                      Release
                    </button>
                    <button
                      onClick={() => onRefund(escrow.id)}
                      disabled={processing}
                      className="text-red-600 hover:text-red-700 disabled:opacity-50"
                    >
                      Refund
                    </button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface PaymentMethodsTabProps {
  methods: PaymentMethod[];
  onAdd: () => void;
  onDelete: (id: string) => void;
  onSetDefault: (id: string) => void;
}

function PaymentMethodsTab({ methods, onAdd, onDelete, onSetDefault }: PaymentMethodsTabProps) {
  return (
    <div>
      <div className="mb-4 flex justify-end">
        <button
          onClick={onAdd}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Add Payment Method
        </button>
      </div>

      {methods.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <p>No payment methods added</p>
          <p className="text-sm mt-2">Add a card to start funding milestones</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {methods.map((method) => (
            <div
              key={method.id}
              className="bg-white rounded-lg shadow p-4 flex items-center justify-between"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-8 bg-gray-100 rounded flex items-center justify-center text-xs font-bold">
                  {method.card_brand?.toUpperCase() || method.type.toUpperCase()}
                </div>
                <div>
                  <p className="font-medium">
                    {method.card_brand} ****{method.card_last4}
                    {method.is_default && (
                      <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                        Default
                      </span>
                    )}
                  </p>
                  {method.card_exp && (
                    <p className="text-sm text-gray-500">Expires {method.card_exp}</p>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                {!method.is_default && (
                  <button
                    onClick={() => onSetDefault(method.id)}
                    className="text-blue-600 hover:text-blue-700 text-sm"
                  >
                    Set Default
                  </button>
                )}
                <button
                  onClick={() => onDelete(method.id)}
                  className="text-red-600 hover:text-red-700 text-sm"
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface TransactionHistoryTabProps {
  transactions: Transaction[];
}

function TransactionHistoryTab({ transactions }: TransactionHistoryTabProps) {
  if (transactions.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <p>No transactions yet</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {transactions.map((tx) => (
            <tr key={tx.id}>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {getTransactionTypeLabel(tx.type)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                {formatCurrency(tx.amount_cents, tx.currency)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span
                  className={`px-2 py-1 text-xs font-medium rounded-full ${
                    tx.status === 'completed'
                      ? 'bg-green-100 text-green-800'
                      : tx.status === 'failed'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-yellow-100 text-yellow-800'
                  }`}
                >
                  {tx.status}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {tx.description || '-'}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {new Date(tx.created_at).toLocaleDateString()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface StatusBadgeProps {
  status: string;
}

function StatusBadge({ status }: StatusBadgeProps) {
  const color = getEscrowStatusColor(status as Parameters<typeof getEscrowStatusColor>[0]);
  const colorClasses: Record<string, string> = {
    green: 'bg-green-100 text-green-800',
    yellow: 'bg-yellow-100 text-yellow-800',
    blue: 'bg-blue-100 text-blue-800',
    red: 'bg-red-100 text-red-800',
    orange: 'bg-orange-100 text-orange-800',
    gray: 'bg-gray-100 text-gray-800',
  };

  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${colorClasses[color] || colorClasses.gray}`}>
      {getEscrowStatusLabel(status as Parameters<typeof getEscrowStatusLabel>[0])}
    </span>
  );
}

interface SetupBillingModalProps {
  onClose: () => void;
  onSubmit: (data: CreateCustomerRequest) => void;
  processing: boolean;
}

function SetupBillingModal({ onClose, onSubmit, processing }: SetupBillingModalProps) {
  const [billingEmail, setBillingEmail] = useState('');
  const [billingName, setBillingName] = useState('');
  const [vatNumber, setVatNumber] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      billing_email: billingEmail,
      billing_name: billingName,
      vat_number: vatNumber || undefined,
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
        <h2 className="text-xl font-bold mb-6">Set Up Billing</h2>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Billing Email *
            </label>
            <input
              type="email"
              value={billingEmail}
              onChange={(e) => setBillingEmail(e.target.value)}
              required
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="billing@company.com"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Billing Name *
            </label>
            <input
              type="text"
              value={billingName}
              onChange={(e) => setBillingName(e.target.value)}
              required
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Company Name Ltd"
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              VAT Number (Optional)
            </label>
            <input
              type="text"
              value={vatNumber}
              onChange={(e) => setVatNumber(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="IE1234567X"
            />
          </div>

          <div className="flex gap-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              disabled={processing}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              disabled={processing || !billingEmail || !billingName}
            >
              {processing ? 'Creating...' : 'Create Account'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface AddPaymentMethodModalProps {
  onClose: () => void;
  onSubmit: (paymentMethodId: string) => void;
  processing: boolean;
}

function AddPaymentMethodModal({ onClose, onSubmit, processing }: AddPaymentMethodModalProps) {
  // In a real implementation, this would use Stripe Elements
  // For now, we'll show a placeholder
  const [paymentMethodId, setPaymentMethodId] = useState('');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
        <h2 className="text-xl font-bold mb-6">Add Payment Method</h2>

        <div className="mb-6 p-4 bg-blue-50 rounded-lg text-sm text-blue-700">
          <p>
            In production, Stripe Elements would be embedded here to securely
            collect card details.
          </p>
        </div>

        {/* Placeholder for Stripe Elements */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Stripe Payment Method ID (for testing)
          </label>
          <input
            type="text"
            value={paymentMethodId}
            onChange={(e) => setPaymentMethodId(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="pm_test_..."
          />
        </div>

        <div className="flex gap-4">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            disabled={processing}
          >
            Cancel
          </button>
          <button
            onClick={() => paymentMethodId && onSubmit(paymentMethodId)}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            disabled={processing || !paymentMethodId}
          >
            {processing ? 'Adding...' : 'Add Card'}
          </button>
        </div>
      </div>
    </div>
  );
}
